import React, { useState } from 'react';
import { v4 as uuidv4 } from 'uuid';
import {
    IPlotlyComponent,
    checkboxDataGenerator,
    capitalizeFirstLetter,
    Pages,
    customTitleGenerator,
    dynamicCheckboxVisibility,
    TestIds,
} from 'utils';
import { WppTypography } from '@wppopen/components-library-react';
import { Checkbox, SideModal } from 'components';
import PlotlyGraph from './PlotlyGraph/PlotlyGraph';
import { useLegacyEffect } from 'hooks';
import { MainLoader } from 'components';
import {
    Main,
    MainWrapper,
    Plot,
    PlotHeader,
    Legend,
    LegendTitle,
    LegendCheckboxesSection,
    LegendMore,
    LegendWrapper,
    ChildrenWrapper,
    LegendListWrapper,
} from './PlotlyComponentStyles';

const loaderTextSchema: any = {
    [Pages.PROJECTION]: {
        header: 'Running',
        body: 'This may take a few minutes. Please stay within the app',
    },
    [Pages.CREATE_SCENARIO]: {
        header: 'The future is being simulated',
        body: 'This may take a few minutes. Please stay within the app',
    },
};

export const PlotlyComponent = ({
    plotlyData,
    plotlyTitle,
    plotHeight = 400,
    legendTitle,
    legendPosition = 'right',
    sideModalTitle,
    localization,
    isLoading,
    page,
    isLegendShown = true,
    isLegendsListShown = true,
    children = null,
}: IPlotlyComponent) => {
    const [currentData, setCurrentData] = useState(plotlyData);
    const [isModalShowing, setIsModalShowing] = useState(false);
    const sideModalHandler = () => setIsModalShowing(true);

    const checkboxHandler = (name: string) => {
        setCurrentData((prevData: any) => dynamicCheckboxVisibility(prevData, name));
    };
    const checkboxData = checkboxDataGenerator(currentData, legendTitle);
    const capitalizedLegendTitle = customTitleGenerator(true, capitalizeFirstLetter(legendTitle));

    useLegacyEffect(() => {
        setCurrentData(plotlyData);
    }, [plotlyData]);

    return (
        <>
            <Main
                legendPosition={legendPosition}
                data-testid={TestIds.projection.plotlyContainer.plot}
            >
                <Plot
                    id={`PlotGraphContainer-${legendPosition}`}
                    legendPosition={legendPosition}
                    data-testid={TestIds.projection.plotlyContainer.graph}
                >
                    <MainWrapper>
                        {plotlyTitle && (
                            <PlotHeader>
                                <WppTypography type="s-strong">{plotlyTitle}</WppTypography>
                            </PlotHeader>
                        )}
                        {currentData?.map((plot: any) => (
                            <MainLoader
                                height={plotHeight}
                                isLoading={isLoading}
                                header={loaderTextSchema[page].header}
                                text={loaderTextSchema[page].body}
                                key={uuidv4()}
                                withInnerContainer={page !== Pages.PROJECTION}
                            >
                                <>
                                    {plot?.header && (
                                        <PlotHeader key={uuidv4()}>{plot?.header}</PlotHeader>
                                    )}
                                    <PlotlyGraph
                                        data={plot?.data}
                                        layout={plot?.layout}
                                        frames={plot?.frames}
                                        height={plotHeight}
                                        key={uuidv4()}
                                        legendPosition={legendPosition}
                                    />
                                </>
                            </MainLoader>
                        ))}
                    </MainWrapper>
                </Plot>
                <Legend isLegendShown={isLegendShown} legendPosition={legendPosition}>
                    <ChildrenWrapper isVisible={legendPosition === 'top'}>
                        {children}
                    </ChildrenWrapper>
                    <LegendWrapper
                        legendPosition={legendPosition}
                        data-testid={TestIds.projection.plotlyContainer.legends}
                    >
                        <LegendTitle
                            type="xs-strong"
                            isVisible={legendPosition !== 'top'}
                            data-testid={TestIds.projection.plotlyContainer.legendstitle}
                        >
                            {capitalizedLegendTitle}
                        </LegendTitle>
                        <LegendListWrapper
                            legendPosition={legendPosition}
                            isLegendsListShown={isLegendsListShown}
                        >
                            <LegendCheckboxesSection legendPosition={legendPosition}>
                                {checkboxData.map((element: any, idx: number) => (
                                    <Checkbox
                                        checked={element.visible}
                                        key={uuidv4()}
                                        checkboxColor={element.fillcolor}
                                        onChange={() => {
                                            checkboxHandler(element.name);
                                        }}
                                        label={element.name}
                                        visibility={
                                            checkboxData.length === 0 ? 'hidden' : 'visible'
                                        }
                                        testid={`PLOTLY.CONTAINER.LEGEND.ITEM-${idx}`}
                                    />
                                ))}
                            </LegendCheckboxesSection>
                        </LegendListWrapper>
                        <LegendMore
                            type="xs-body"
                            id="learn-more"
                            onClick={sideModalHandler}
                            data-testid="legendLearnMore"
                            isLearnMoreShown={legendPosition === 'right'}
                        >
                            Learn more
                        </LegendMore>
                    </LegendWrapper>
                </Legend>
            </Main>
            <SideModal
                isSidebarShown={isModalShowing}
                title={capitalizeFirstLetter(sideModalTitle)}
                setIsSidebarShown={setIsModalShowing}
                localization={localization}
            />
        </>
    );
};
